/**
 * DocApiClient - Document API Client for Background Cloud Save Queue
 *
 * Provides HTTP client for document CRUD operations with optimistic locking support.
 * Handles HTTP error codes and converts them to FlushError types.
 *
 * @module features/storage/docApiClient
 * @see SPEC-006-background-cloud-save-queue
 * @see Requirements 2.4, 2.5, 4.1, 4.2
 */

import type { FlushError, FlushErrorType } from './types';

/**
 * Request payload for PUT /docs/:id
 *
 * @see Requirements 2.4, 2.5
 */
export interface PutDocRequest {
  /** Full markdown content to save */
  markdown: string;
  /** Version ID for If-Match header (optimistic locking) */
  baseVersionId: string;
  /** Optional metadata updates */
  meta?: {
    /** Document title (max 200 characters) */
    title?: string;
  };
}

/**
 * Response from PUT /docs/:id
 */
export interface PutDocResponse {
  /** New version ID after successful save */
  versionId: string;
}

/**
 * Response from GET /docs/:id
 */
export interface GetDocResponse {
  /** Document snapshot with markdown content */
  snapshot: {
    markdown: string;
  };
  /** Document metadata */
  meta: {
    docId: string;
    title: string;
    ownerId: string;
    createdAt: number;
    updatedAt: number;
  };
  /** Current version ID */
  versionId: string;
}

/**
 * Options for DocApiClient
 */
export interface DocApiClientOptions {
  /** Request timeout in milliseconds (default: 30000) */
  timeoutMs?: number;
}

/**
 * Default timeout for API requests (30 seconds)
 * @see Requirements 6.7
 */
const DEFAULT_TIMEOUT_MS = 30000;

/**
 * Maps HTTP status codes to FlushErrorType
 *
 * @param statusCode - HTTP status code
 * @returns Corresponding FlushErrorType
 */
function mapStatusCodeToErrorType(statusCode: number): FlushErrorType {
  if (statusCode === 401 || statusCode === 403) {
    return 'unauthorized';
  }
  if (statusCode === 409 || statusCode === 412) {
    // 409 Conflict or 412 Precondition Failed = version conflict
    return 'conflict';
  }
  if (statusCode >= 500) {
    return 'server';
  }
  return 'network';
}

/**
 * Creates a FlushError from an HTTP response
 *
 * @param statusCode - HTTP status code
 * @param message - Error message
 * @returns FlushError object
 */
function createFlushError(statusCode: number, message: string): FlushError {
  return {
    type: mapStatusCodeToErrorType(statusCode),
    message,
    statusCode,
  };
}

/**
 * DocApiClient - HTTP client for document operations
 *
 * Features:
 * - Optimistic locking via If-Match header
 * - Automatic error type mapping
 * - Request timeout handling
 * - Authentication token injection
 *
 * @see Requirements 2.4, 2.5, 4.1, 4.2
 */
export class DocApiClient {
  private readonly timeoutMs: number;

  /**
   * Creates a new DocApiClient instance
   *
   * @param baseUrl - Base URL for the API (e.g., 'https://api.linkode.app')
   * @param getAuthToken - Async function to retrieve the current auth token
   * @param options - Optional configuration
   */
  constructor(
    private readonly baseUrl: string,
    private readonly getAuthToken: () => Promise<string>,
    options: DocApiClientOptions = {}
  ) {
    this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  }

  /**
   * Save document content with optimistic locking
   *
   * Sends PUT request to /docs/:id with If-Match header for version control.
   * Returns HTTP 412 Precondition Failed if version conflict occurs.
   *
   * @param docId - Document ID to update
   * @param request - Request payload with markdown and baseVersionId
   * @returns Promise resolving to new versionId on success
   * @throws FlushError on failure (network, conflict, unauthorized, server, timeout)
   *
   * @see Requirements 2.4, 2.5, 4.1
   */
  async putDoc(docId: string, request: PutDocRequest): Promise<PutDocResponse> {
    const url = `${this.baseUrl}/docs/${encodeURIComponent(docId)}`;
    const token = await this.getAuthToken();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'If-Match': request.baseVersionId,
        },
        body: JSON.stringify({
          markdown: request.markdown,
          ...(request.meta && { meta: request.meta }),
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorMessage = await this.extractErrorMessage(response);
        throw createFlushError(response.status, errorMessage);
      }

      const data = (await response.json()) as PutDocResponse;
      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      // Re-throw FlushError as-is
      if (this.isFlushError(error)) {
        throw error;
      }

      // Handle abort (timeout)
      if (error instanceof DOMException && error.name === 'AbortError') {
        const timeoutError: FlushError = {
          type: 'timeout',
          message: `Request timed out after ${this.timeoutMs}ms`,
        };
        throw timeoutError;
      }

      // Handle network errors
      const networkError: FlushError = {
        type: 'network',
        message: error instanceof Error ? error.message : 'Network request failed',
      };
      throw networkError;
    }
  }

  /**
   * Fetch document content and metadata
   *
   * Used to retrieve the latest version when a conflict is detected.
   *
   * @param docId - Document ID to fetch
   * @returns Promise resolving to document snapshot, meta, and versionId
   * @throws FlushError on failure
   *
   * @see Requirements 4.2
   */
  async getDoc(docId: string): Promise<GetDocResponse> {
    const url = `${this.baseUrl}/docs/${encodeURIComponent(docId)}`;
    const token = await this.getAuthToken();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorMessage = await this.extractErrorMessage(response);
        throw createFlushError(response.status, errorMessage);
      }

      const data = (await response.json()) as GetDocResponse;
      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      // Re-throw FlushError as-is
      if (this.isFlushError(error)) {
        throw error;
      }

      // Handle abort (timeout)
      if (error instanceof DOMException && error.name === 'AbortError') {
        const timeoutError: FlushError = {
          type: 'timeout',
          message: `Request timed out after ${this.timeoutMs}ms`,
        };
        throw timeoutError;
      }

      // Handle network errors
      const networkError: FlushError = {
        type: 'network',
        message: error instanceof Error ? error.message : 'Network request failed',
      };
      throw networkError;
    }
  }

  /**
   * Extract error message from HTTP response
   *
   * Attempts to parse JSON error body, falls back to status text.
   */
  private async extractErrorMessage(response: Response): Promise<string> {
    try {
      const body = await response.json();
      if (typeof body === 'object' && body !== null) {
        // Try common error message fields
        if (typeof body.message === 'string') return body.message;
        if (typeof body.error === 'string') return body.error;
        if (typeof body.detail === 'string') return body.detail;
      }
    } catch {
      // JSON parsing failed, use status text
    }
    return response.statusText || `HTTP ${response.status}`;
  }

  /**
   * Type guard for FlushError
   */
  private isFlushError(error: unknown): error is FlushError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'type' in error &&
      'message' in error &&
      typeof (error as FlushError).type === 'string' &&
      typeof (error as FlushError).message === 'string'
    );
  }
}
